'use client'

import * as React from 'react'
import { useHydrated } from 'hooks/use-hydrated'

import type { ClientVariant } from '../types'
import { hasElementBeenEdgeTrimmed } from '../utils/has-element-been-edge-trimmed'
import {
  isClientSideVariantSelection,
  isEdgeVariantSelection,
  isHtmlRenderVariantSelection,
} from '../utils/rendering-selection-state'
import { getVariantExistingStyle } from '../utils/get-variant-existing-style'
import { createVariantStyleObserver } from '../utils/create-variant-style-observer'
import { useExperimentEvents } from '../hooks/use-experiment-events'

export const StaticVariant = ({
  variant,
  renderingId,
}: {
  variant: ClientVariant
  renderingId: string
}) => {
  const isHydrated = useHydrated()
  const htmlId = `${renderingId}-${variant.id}`
  const [isNonEdgeVisible, setIsNonEdgeVisible] = React.useState(
    () => !!getVariantExistingStyle(variant.id, renderingId),
  )
  const observerRef = React.useRef<MutationObserver | null>(null)
  useExperimentEvents(variant.conditions, htmlId, isNonEdgeVisible)

  // This will not do anything if behing a cloudflare worker
  React.useEffect(() => {
    if (
      !isHydrated ||
      isEdgeVariantSelection() ||
      (!isClientSideVariantSelection() && !isHtmlRenderVariantSelection())
    ) {
      return
    }

    setIsNonEdgeVisible(!!getVariantExistingStyle(variant.id, renderingId))

    if (!observerRef.current) {
      observerRef.current = createVariantStyleObserver(variant.id, renderingId, setIsNonEdgeVisible)
    }

    return () => observerRef.current?.disconnect()
  }, [isHydrated, variant.id, renderingId])

  let staticVariantClasses = `!hidden static-variant-element static-variant-${htmlId}`

  if (variant.isDefault) {
    staticVariantClasses += ` render-default`
  }

  if (hasElementBeenEdgeTrimmed(htmlId)) {
    return null
  }

  if (isEdgeVariantSelection()) {
    staticVariantClasses = 'render-default'
  }

  const wrapperProps = {
    suppressHydrationWarning: true,
    'data-rendering-id': renderingId,
    id: htmlId,
    className: staticVariantClasses,
  }

  if (!isHydrated) {
    return (
      <span
        {...wrapperProps}
        // HYDRATION-SAFE APPROACH: Using prebuilt static HTML to avoid hydration mismatches
        // This HTML was generated at build time using renderToStaticMarkup(), ensuring
        // identical content regardless of server/client cookie access differences.
        // By injecting prebuilt HTML instead of rendering React components initially,
        // we bypass React's hydration process entirely, eliminating mismatch errors
        // when edge-side logic modifies/eliminates html
        dangerouslySetInnerHTML={{
          __html: variant.rawHtml,
        }}
      />
    )
  }

  if (isClientSideVariantSelection() || isHtmlRenderVariantSelection()) {
    // Use the reactive state instead of querying DOM every render
    // oxlint-disable-next-line unicorn/no-lonely-if
    if (!isNonEdgeVisible) {
      return null
    }
  }

  return <span {...wrapperProps}>{variant.content}</span>
}
